import { defineStore } from "pinia";
import {
  api,
  type Result,
  Success,
  watchRequest,
  type ResultOkRef,
  type QueryParams,
} from "../api";
import {
  RouteResolveSkippedPayloadSchema,
  RouteLoadPayloadSchema,
  RouteProgressPayloadSchema,
  RoutePreviewResponseSchema,
  RoutePreviewDraftResponseSchema,
  RoutePreviewDraftSchema,
  type RouteResolveSkippedPayload,
  type RouteLoadPayload,
  type RouteProgressPayload,
  type RoutePreviewPayload,
  type RoutePreviewResponse,
  type RoutePreviewDraft,
  type RoutePreviewDraftResponse,
  RouteResponseSchema,
  type RoutePayload,
  type RouteResponse,
} from "../schemas/route";
import { PaginatedSchema, type Paginated } from "../schemas/paginated";
import { markRaw } from "vue";
import type { OrderAdminPayload } from "../schemas/order/form";

class RouteApi {
  private readonly url = "/routes/";
  private readonly createUrl = "/routes/create/";
  private readonly previewUrl = "/routes/preview-v2/";
  private readonly previewDraftUrl = "/routes/preview-draft/";
  private readonly resolveSkippedUrl = (routeId: number) =>
    `/routes/${routeId}/resolve-skipped/`;
  private readonly progressUrl = (routeId: number) =>
    `/routes/${routeId}/progress/`;
  private readonly addOrderUrl = (routeId: number) =>
    `/routes/${routeId}/add-order/`;
  private readonly loadUrl = (routeId: number) =>
    `/routes/${routeId}/load/`;

  getRoutes(
    filters?: QueryParams,
  ): ResultOkRef<Success<Paginated<RouteResponse[]>>> {
    return watchRequest(
      this.url,
      PaginatedSchema(RouteResponseSchema),
      filters,
    );
  }

  getRoute(routeId: number): ResultOkRef<Success<RouteResponse>> {
    return watchRequest(`${this.url}${routeId}/`, RouteResponseSchema);
  }

  createRoute(payload: RoutePayload): Promise<Result<RouteResponse[]>> {
    return api.post(this.createUrl, RouteResponseSchema.array(), payload);
  }

  addOrderToRoute(
    routeId: number,
    payload: OrderAdminPayload,
  ): Promise<Result<RouteResponse>> {
    return api.post(this.addOrderUrl(routeId), RouteResponseSchema, payload);
  }

  updateLoad(
    routeId: number,
    payload: RouteLoadPayload,
  ): Promise<Result<RouteResponse>> {
    return api.patch(
      this.loadUrl(routeId),
      RouteResponseSchema,
      RouteLoadPayloadSchema.parse(payload),
    );
  }

  previewRoute(
    payload: RoutePreviewPayload,
  ): Promise<Result<RoutePreviewResponse>> {
    return api.post(this.previewUrl, RoutePreviewResponseSchema, payload);
  }

  getPreviewDraft(): Promise<Result<RoutePreviewDraftResponse>> {
    return api.get(this.previewDraftUrl, RoutePreviewDraftResponseSchema);
  }

  savePreviewDraft(
    payload: RoutePreviewDraft,
  ): Promise<Result<RoutePreviewDraft>> {
    return api.post(
      this.previewDraftUrl,
      RoutePreviewDraftSchema,
      RoutePreviewDraftSchema.parse(payload),
    );
  }

  clearPreviewDraft(): Promise<Result<void>> {
    return api.delete(this.previewDraftUrl);
  }

  resolveSkipped(
    routeId: number,
    payload: RouteResolveSkippedPayload,
  ): Promise<Result<RouteResponse>> {
    return api.post(
      this.resolveSkippedUrl(routeId),
      RouteResponseSchema,
      RouteResolveSkippedPayloadSchema.parse(payload),
    );
  }

  updateProgress(
    routeId: number,
    payload: RouteProgressPayload,
  ): Promise<Result<RouteResponse>> {
    return api.post(
      this.progressUrl(routeId),
      RouteResponseSchema,
      RouteProgressPayloadSchema.parse(payload),
    );
  }
}

const store = defineStore("route", () => ({
  api: markRaw(new RouteApi()),
}));

export function useRouteApi() {
  return store().api;
}
